import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { WeaveThemeContext } from './WeaveThemeContext';
import { INFO360_THEMES } from '../theme.types';
import { useThemeVariables } from '../../../../utils';
import { ThemeContextValue } from '@weave-design/theme-context';
import { weaveThemes } from '../weave.types';
import { useSelectFeaturesLoaded } from '../../../../selectors/app.selectors';
import cookies from 'browser-cookies';

// helper functions
const setThemeCookie = (theme: INFO360_THEMES) => {
	cookies.set('theme', theme, {
		domain: window.location.hostname,
		expires: 365,
		samesite: 'Lax',
		secure: true,
	});
};
const getThemeCookie = () => {
	return cookies.get('theme') as INFO360_THEMES;
};

// WeaveThemeProvider component

interface WeaveThemeProviderProps {
	children: ReactNode;
}
const WeaveThemeProvider = ({ children }: WeaveThemeProviderProps) => {
	const { displayThemePicker } = useThemeVariables();
	const isLoading = !useSelectFeaturesLoaded();
	const channel = new BroadcastChannel('weave-theme-channel');

	const [theme, setWeaveTheme] = useState<INFO360_THEMES>(
		getThemeCookie() ?? INFO360_THEMES.Classic,
	);
	const [weaveThemeConsumer, setWeaveThemeConsumer] =
		useState<ThemeContextValue>(weaveThemes[theme]);

	const isWeaveTheme = theme !== INFO360_THEMES.Classic;

	const setTheme = useCallback(
		(newTheme: INFO360_THEMES, skipCookieUpdate?: boolean) => {
			setWeaveTheme(newTheme);
			setWeaveThemeConsumer(weaveThemes[newTheme]);
			if (!skipCookieUpdate) {
				setThemeCookie(newTheme);
				channel?.postMessage({
					type: 'THEME_CHANGE',
					theme: newTheme,
				});
			}
		},
		[weaveThemes, channel],
	);

	// Effects
	useEffect(() => {
		//Listen for theme changes from other tabs
		const handleThemeChange = (event: MessageEvent) => {
			if (event.data?.type === 'THEME_CHANGE') {
				setTheme(event.data.theme, true);
			}
		};
		channel.addEventListener('message', handleThemeChange);
		return () => {
			channel.removeEventListener('message', handleThemeChange);
			channel.close();
		};
	}, []);
	useEffect(() => {
		// Initialize
		const cookieTheme = getThemeCookie();
		const displayClassic =
			(isLoading && !cookieTheme) || (!displayThemePicker && !isLoading);

		const themeValue = displayClassic
			? INFO360_THEMES.Classic
			: cookieTheme ?? INFO360_THEMES.Light;
		setTheme(themeValue, isLoading);
	}, [displayThemePicker, isLoading]);
	// End Effects

	// Render
	return (
		<WeaveThemeContext.Provider
			value={{
				theme,
				setTheme,
				displayThemePicker,
				weaveThemeConsumer,
				isWeaveTheme,
			}}>
			{children}
		</WeaveThemeContext.Provider>
	);
};

export default WeaveThemeProvider;
